import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Play, Pause, Square, Volume2, Settings } from 'lucide-react';
import { useSpeechSynthesis } from '../hooks/useSpeechSynthesis';

interface SpeechControlsProps {
  text: string;
  title?: string;
  className?: string;
}

export const SpeechControls: React.FC<SpeechControlsProps> = ({
  text,
  title = 'Contenu',
  className = '',
}) => {
  const { t, i18n } = useTranslation();
  const {
    isSupported,
    isPlaying,
    isPaused,
    voices,
    currentVoice,
    speak,
    pause,
    resume,
    stop,
  } = useSpeechSynthesis(i18n.language);
  const [showSettings, setShowSettings] = useState(false);
  const [rate, setRate] = useState(1);
  const [pitch, setPitch] = useState(1);
  const [volume, setVolume] = useState(1);

  // Si l'API n'est pas supportée, ne rien afficher
  if (!isSupported) {
    return null;
  }

  const handlePlay = () => {
    if (isPaused) {
      resume();
    } else if (isPlaying) {
      pause();
    } else {
      speak(text, {
        rate,
        pitch,
        volume,
        voice: currentVoice,
      });
    }
  };

  const handleStop = () => {
    stop();
  };

  // Filtrer les voix selon la langue actuelle
  const getVoicesForCurrentLanguage = () => {
    const currentLang = i18n.language;
    return voices.filter((voice) => {
      switch (currentLang) {
        case 'fr':
          return (
            voice.lang.startsWith('fr') ||
            voice.name.toLowerCase().includes('french')
          );
        case 'en':
          return (
            voice.lang.startsWith('en') ||
            voice.name.toLowerCase().includes('english')
          );
        case 'es':
          return (
            voice.lang.startsWith('es') ||
            voice.name.toLowerCase().includes('spanish')
          );
        case 'zh':
          return (
            voice.lang.startsWith('zh') ||
            voice.name.toLowerCase().includes('chinese')
          );
        default:
          return (
            voice.lang.startsWith('fr') ||
            voice.name.toLowerCase().includes('french')
          );
      }
    });
  };

  const currentLanguageVoices = getVoicesForCurrentLanguage();

  return (
    <div className={`flex items-center gap-2 ${className}`}>
      {/* Contrôles principaux */}
      <div className="flex items-center gap-1">
        <button
          onClick={handlePlay}
          className="p-2 text-gray-600 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-400 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          title={
            isPaused
              ? t('summary.form.speech.resume')
              : isPlaying
                ? t('summary.form.speech.pause')
                : `${t('summary.form.speech.play')} ${title}`
          }
          aria-label={
            isPaused
              ? t('summary.form.speech.resume')
              : isPlaying
                ? t('summary.form.speech.pause')
                : `${t('summary.form.speech.play')} ${title}`
          }
        >
          {isPaused ? (
            <Play className="h-4 w-4" />
          ) : isPlaying ? (
            <Pause className="h-4 w-4" />
          ) : (
            <Play className="h-4 w-4" />
          )}
        </button>

        {isPlaying && (
          <button
            onClick={handleStop}
            className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 dark:text-gray-400 dark:hover:text-red-400 dark:hover:bg-red-900/30 rounded-lg transition-colors"
            title={t('summary.form.speech.stop')}
            aria-label={t('summary.form.speech.stop')}
          >
            <Square className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Indicateur de lecture */}
      {isPlaying && (
        <div className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400">
          <div className="flex space-x-1">
            <div className="w-1 h-3 bg-blue-500 rounded-full animate-pulse"></div>
            <div
              className="w-1 h-3 bg-blue-500 rounded-full animate-pulse"
              style={{ animationDelay: '0.1s' }}
            ></div>
            <div
              className="w-1 h-3 bg-blue-500 rounded-full animate-pulse"
              style={{ animationDelay: '0.2s' }}
            ></div>
          </div>
          <span className="text-xs">{t('summary.form.speech.reading')}</span>
        </div>
      )}

      {/* Bouton paramètres */}
      <div className="relative">
        <button
          onClick={() => setShowSettings(!showSettings)}
          className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-50 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
          title={t('summary.form.speech.settings')}
          aria-label={t('summary.form.speech.settings')}
        >
          <Settings className="h-4 w-4" />
        </button>

        {/* Panneau des paramètres */}
        {showSettings && (
          <div className="absolute right-0 top-full mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg p-4 z-10">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Paramètres de voix
            </h3>

            {/* Sélection de voix */}
            {currentLanguageVoices.length > 0 && (
              <div className="mb-3">
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('summary.form.speech.voice')}
                </label>
                <select
                  value={currentVoice?.name || ''}
                  onChange={(e) => {
                    const selectedVoice = voices.find(
                      (voice) => voice.name === e.target.value
                    );
                    if (selectedVoice) {
                      speak(text, {
                        rate,
                        pitch,
                        volume,
                        voice: selectedVoice,
                      });
                    }
                  }}
                  className="w-full text-sm border border-gray-200 dark:border-gray-600 rounded-md px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                >
                  {currentLanguageVoices.map((voice) => (
                    <option key={voice.name} value={voice.name}>
                      {voice.name} ({voice.lang})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Vitesse */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('summary.form.speech.speed')}: {rate.toFixed(1)}x
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={rate}
                onChange={(e) => setRate(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Hauteur */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('summary.form.speech.pitch')}: {pitch.toFixed(1)}
              </label>
              <input
                type="range"
                min="0.5"
                max="2"
                step="0.1"
                value={pitch}
                onChange={(e) => setPitch(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Volume */}
            <div className="mb-3">
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                {t('summary.form.speech.volume')}: {Math.round(volume * 100)}%
              </label>
              <input
                type="range"
                min="0"
                max="1"
                step="0.1"
                value={volume}
                onChange={(e) => setVolume(parseFloat(e.target.value))}
                className="w-full"
              />
            </div>

            {/* Bouton de test */}
            <button
              onClick={() =>
                speak('Test de la voix avec les nouveaux paramètres.', {
                  rate,
                  pitch,
                  volume,
                  voice: currentVoice,
                })
              }
              className="w-full text-xs bg-blue-600 text-white px-3 py-2 rounded-md hover:bg-blue-700 transition-colors"
            >
              <Volume2 className="h-3 w-3 inline mr-1" />
              {t('summary.form.speech.testVoice')}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};
